import type { Creator } from '@fluenceur/types';
import { useMemo } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { useFetchCreator } from '@/hooks/queries/use-fetch-creator';

interface UseProposeCreatorResolutionArgs {
  /** The click intent: nothing resolves (and nothing fetches) until true. */
  requested: boolean;
  /** Resolved identity of the recipient; `null` for a raw-email recipient. */
  identityId: string | null;
  /**
   * Social profile id when the caller ALREADY knows it (the contact picker's
   * `ContactSearchOption.socialProfileId` contract): skips the contact-summary
   * hop entirely. Pass `null` for "known: none" (email-only contact), leave
   * `undefined` when unknown — the summary then resolves it.
   */
  knownSocialProfileId?: string | null;
  /** Preferred platform when picking a profile from the summary (contact drawer). */
  preferredPlatform?: string | null;
}

interface ProposeCreatorResolution {
  /** The enriched Creator, once resolved. `undefined` while in flight or absent. */
  creator: Creator | undefined;
  /**
   * True while the enrichment is settling (summary and/or creator fetch). The
   * caller must hold the wizard CLOSED until this is false: opening earlier
   * races the provider's one-shot seed, which would then run without the
   * creator and dead-end the Affiliation type for an email-less recipient.
   */
  isResolving: boolean;
}

/**
 * Shared "resolve, then open" enrichment for the propose-a-collaboration
 * entries (collabs picker, contact card): click intent → primary social profile
 * → `useFetchCreator` → `{ creator, isResolving }`. Both hops are cache-backed
 * React Query reads, so a warm cache resolves synchronously. Callers keep their
 * own eligibility, gating and modal wiring.
 */
export const useProposeCreatorResolution = ({
  requested,
  identityId,
  knownSocialProfileId,
  preferredPlatform,
}: UseProposeCreatorResolutionArgs): ProposeCreatorResolution => {
  const { user } = useAuth();
  // Only hop through the summary when the caller could not supply the profile
  // id itself (`undefined`); `null` means "known: none" and skips it too.
  const needsSummary = knownSocialProfileId === undefined;
  const { data: summary, isLoading: isSummaryLoading } = useContactSummaryQuery(
    user?.currentBrandId,
    requested && needsSummary ? identityId : null
  );

  const socialProfileId = useMemo(() => {
    if (!needsSummary) return knownSocialProfileId ?? null;
    const profiles = summary?.socialProfiles ?? [];
    const preferred = profiles.find((profile) => profile.platform === preferredPlatform);
    return (preferred ?? profiles[0])?.socialProfileId ?? null;
  }, [needsSummary, knownSocialProfileId, summary, preferredPlatform]);

  const { data: creator, isLoading: isCreatorLoading } = useFetchCreator(socialProfileId ?? '', {
    enabled: requested && !!socialProfileId,
  });

  const isResolving =
    requested &&
    !!identityId &&
    ((needsSummary && isSummaryLoading) || (!!socialProfileId && isCreatorLoading));

  return { creator, isResolving };
};
